/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Alert,
  Skeleton,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { AutoFixHigh as SuggestIcon, Memory as CpuIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { computeSpecsApi } from '../../services/api';
import type { ComputeSpec, ComputeSuggestion } from '../../types';

interface ComputeSpecSelectorProps {
  value: number | null;
  onChange: (specId: number | null) => void;
  jobType?: string;
  jobId?: number;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

const EXECUTOR_LABELS: Record<string, string> = {
  cpu: 'CPU',
  gpu: 'GPU',
};

const MODE_LABELS: Record<string, string> = {
  local: 'Local',
  remote_vm: 'Remote VM',
};

export const ComputeSpecSelector: React.FC<ComputeSpecSelectorProps> = ({
  value,
  onChange,
  jobType,
  jobId,
  disabled = false,
  size = 'small',
}) => {
  const [suggestion, setSuggestion] = useState<ComputeSuggestion | null>(null);

  const { data: specs, isLoading } = useQuery({
    queryKey: ['computeSpecs'],
    queryFn: () => computeSpecsApi.list(),
  });

  const { data: suggestData, isFetching: suggestLoading } = useQuery({
    queryKey: ['computeSuggestion', jobType, jobId],
    queryFn: () => computeSpecsApi.suggest(jobType!, jobId!),
    enabled: !!jobType && !!jobId,
  });

  useEffect(() => {
    if (suggestData) {
      setSuggestion(suggestData);
      if (!value && suggestData.suggested_spec) {
        onChange(suggestData.suggested_spec);
      }
    }
  }, [suggestData]);

  const handleApplySuggestion = () => {
    if (suggestion?.suggested_spec) {
      onChange(suggestion.suggested_spec);
    }
  };

  const selectedSpec = specs?.find((s) => s.id === value);

  if (isLoading) {
    return <Skeleton variant="rounded" height={40} />;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <FormControl fullWidth size={size}>
          <InputLabel>Compute Spec</InputLabel>
          <Select
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
            label="Compute Spec"
            disabled={disabled}
          >
            <MenuItem value="">
              <em>Default (local)</em>
            </MenuItem>
            {specs?.map((spec) => (
              <MenuItem key={spec.id} value={spec.id}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <Typography variant="body2">{spec.name}</Typography>
                  <Chip
                    label={MODE_LABELS[spec.compute_mode] ?? spec.compute_mode}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  <Chip
                    label={EXECUTOR_LABELS[spec.executor] ?? spec.executor}
                    size="small"
                    color={spec.executor === 'gpu' ? 'warning' : 'default'}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  {spec.is_preset && (
                    <Chip label="preset" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {jobType && jobId && (
          <Tooltip title="Auto-suggest compute spec">
            <span>
              <IconButton
                onClick={handleApplySuggestion}
                disabled={disabled || suggestLoading || !suggestion?.suggested_spec}
                size={size}
                color="primary"
              >
                <SuggestIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>

      {suggestion && (
        <Alert severity="info" sx={{ mt: 1, py: 0 }} icon={<CpuIcon fontSize="small" />}>
          <Typography variant="caption">
            <strong>Suggestion:</strong> {suggestion.reason}
            {suggestion.estimated_cost_eur != null && (
              <> — est. €{suggestion.estimated_cost_eur.toFixed(2)}</>
            )}
          </Typography>
        </Alert>
      )}

      {selectedSpec?.compute_mode === 'remote_vm' && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          VM type: {selectedSpec.vm_type} · Timeout: {selectedSpec.timeout_hours}h · Cooldown:{' '}
          {selectedSpec.cooldown_minutes}min
          {selectedSpec.max_cost_eur && <> · Max cost: €{selectedSpec.max_cost_eur}</>}
        </Typography>
      )}
    </Box>
  );
};
